import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Loader2, Pencil, ArrowRightLeft } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Rename or merge a vendor into another existing vendor.
 *
 * Props:
 *  - open, onOpenChange, currentVendor { vendor, vendor_key },
 *  - onSuccess({ final_vendor_key, display_name, merged, renamed }) — called after a successful op
 */
export function VendorRenameMergeDialog({ open, onOpenChange, currentVendor, onSuccess }) {
    const [mode, setMode] = useState('rename'); // 'rename' | 'merge'
    const [newName, setNewName] = useState('');
    const [targetKey, setTargetKey] = useState('');
    const [displayNameChoice, setDisplayNameChoice] = useState('target'); // 'source' | 'target' | 'custom'
    const [customName, setCustomName] = useState('');
    const [vendors, setVendors] = useState([]);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // reset on open change
    useEffect(() => {
        if (open) {
            setMode('rename');
            setNewName(currentVendor?.vendor || '');
            setTargetKey('');
            setDisplayNameChoice('target');
            setCustomName('');
        }
    }, [open, currentVendor]);

    // Load vendor list when merge mode is chosen (or on open)
    useEffect(() => {
        if (!open) return;
        if (mode !== 'merge') return;
        if (vendors.length) return;
        setLoadingVendors(true);
        api.listVendors()
            .then((rows) => setVendors(rows || []))
            .catch(() => setVendors([]))
            .finally(() => setLoadingVendors(false));
    }, [open, mode, vendors.length]);

    const targetVendor = useMemo(
        () => vendors.find((v) => v.vendor_key === targetKey) || null,
        [vendors, targetKey],
    );

    const otherVendors = useMemo(
        () => vendors.filter((v) => v.vendor_key !== currentVendor?.vendor_key),
        [vendors, currentVendor],
    );

    const finalDisplayName = useMemo(() => {
        if (mode !== 'merge') return newName.trim();
        if (displayNameChoice === 'source') return currentVendor?.vendor || '';
        if (displayNameChoice === 'target') return targetVendor?.vendor || '';
        return customName.trim();
    }, [mode, newName, displayNameChoice, currentVendor, targetVendor, customName]);

    const canSubmit = useMemo(() => {
        if (submitting) return false;
        if (mode === 'rename') {
            const nm = newName.trim();
            if (!nm) return false;
            // must be different from current name
            if (nm.toLowerCase() === (currentVendor?.vendor || '').trim().toLowerCase()) return false;
            return true;
        }
        // merge
        if (!targetVendor) return false;
        if (!finalDisplayName) return false;
        return true;
    }, [submitting, mode, newName, currentVendor, targetVendor, finalDisplayName]);

    async function handleSubmit() {
        if (!currentVendor?.vendor_key) return;
        setSubmitting(true);
        try {
            let body;
            if (mode === 'rename') {
                body = { new_vendor_name: newName.trim() };
            } else {
                body = {
                    target_vendor_key: targetVendor.vendor_key,
                    display_name: finalDisplayName,
                };
            }
            const res = await api.renameVendor(currentVendor.vendor_key, body);
            toast.success(
                res.merged
                    ? `Merged into "${res.display_name}" · ${res.alerts_recomputed} alerts recomputed`
                    : `Renamed to "${res.display_name}"`,
            );
            onOpenChange(false);
            onSuccess?.(res);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to update vendor');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md"
                data-testid="vendor-rename-merge-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Rename or merge vendor</DialogTitle>
                    <DialogDescription>
                        Fix an OCR misread name, or fold this vendor into another one when they're
                        the same business under different spellings.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="text-sm">
                        <span className="text-muted-foreground">Current name:</span>{' '}
                        <span className="font-medium" data-testid="vendor-rmm-current">
                            {currentVendor?.vendor}
                        </span>
                    </div>

                    <RadioGroup
                        value={mode}
                        onValueChange={setMode}
                        className="grid grid-cols-2 gap-2"
                    >
                        <label
                            htmlFor="mode-rename"
                            className={`cursor-pointer flex items-start gap-2 rounded-md border p-3 text-sm transition-colors ${mode === 'rename' ? 'border-primary bg-accent' : 'border-border hover:bg-muted/40'}`}
                        >
                            <RadioGroupItem
                                value="rename"
                                id="mode-rename"
                                data-testid="vendor-rmm-mode-rename"
                            />
                            <div>
                                <div className="font-medium flex items-center gap-1">
                                    <Pencil size={12} /> Rename
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Change the display name for this vendor.
                                </div>
                            </div>
                        </label>
                        <label
                            htmlFor="mode-merge"
                            className={`cursor-pointer flex items-start gap-2 rounded-md border p-3 text-sm transition-colors ${mode === 'merge' ? 'border-primary bg-accent' : 'border-border hover:bg-muted/40'}`}
                        >
                            <RadioGroupItem
                                value="merge"
                                id="mode-merge"
                                data-testid="vendor-rmm-mode-merge"
                            />
                            <div>
                                <div className="font-medium flex items-center gap-1">
                                    <ArrowRightLeft size={12} /> Merge
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Fold into another vendor's history.
                                </div>
                            </div>
                        </label>
                    </RadioGroup>

                    {mode === 'rename' ? (
                        <div className="space-y-2">
                            <Label htmlFor="rename-input">New vendor name</Label>
                            <Input
                                id="rename-input"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder="e.g. Shiv Gauri Agencies"
                                data-testid="vendor-rmm-new-name"
                                autoFocus
                            />
                            <p className="text-xs text-muted-foreground">
                                Existing bills and price alerts will move to this new name.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="merge-target">Merge into</Label>
                                <Select value={targetKey} onValueChange={setTargetKey}>
                                    <SelectTrigger id="merge-target" data-testid="vendor-rmm-target">
                                        <SelectValue
                                            placeholder={loadingVendors ? 'Loading vendors…' : 'Choose a vendor…'}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {otherVendors.length === 0 ? (
                                            <div className="px-2 py-6 text-xs text-muted-foreground text-center">
                                                No other vendors yet.
                                            </div>
                                        ) : (
                                            otherVendors.map((v) => (
                                                <SelectItem
                                                    key={v.vendor_key}
                                                    value={v.vendor_key}
                                                    data-testid={`vendor-rmm-target-opt-${v.vendor_key}`}
                                                >
                                                    {v.vendor}{' '}
                                                    <span className="text-muted-foreground">
                                                        · {v.bills_count} bill{v.bills_count !== 1 ? 's' : ''}
                                                    </span>
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>

                            {targetVendor && (
                                <div className="space-y-2 rounded-md border border-border p-3 bg-muted/30">
                                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                                        Which display name to keep?
                                    </Label>
                                    <RadioGroup value={displayNameChoice} onValueChange={setDisplayNameChoice}>
                                        <label
                                            htmlFor="dn-target"
                                            className="flex items-center gap-2 text-sm cursor-pointer"
                                        >
                                            <RadioGroupItem
                                                value="target"
                                                id="dn-target"
                                                data-testid="vendor-rmm-dn-target"
                                            />
                                            <span>
                                                Keep <b>{targetVendor.vendor}</b>{' '}
                                                <span className="text-muted-foreground">(target)</span>
                                            </span>
                                        </label>
                                        <label
                                            htmlFor="dn-source"
                                            className="flex items-center gap-2 text-sm cursor-pointer"
                                        >
                                            <RadioGroupItem
                                                value="source"
                                                id="dn-source"
                                                data-testid="vendor-rmm-dn-source"
                                            />
                                            <span>
                                                Use <b>{currentVendor?.vendor}</b>{' '}
                                                <span className="text-muted-foreground">(this vendor)</span>
                                            </span>
                                        </label>
                                        <label
                                            htmlFor="dn-custom"
                                            className="flex items-center gap-2 text-sm cursor-pointer"
                                        >
                                            <RadioGroupItem
                                                value="custom"
                                                id="dn-custom"
                                                data-testid="vendor-rmm-dn-custom"
                                            />
                                            <span>Type a custom name</span>
                                        </label>
                                    </RadioGroup>
                                    {displayNameChoice === 'custom' && (
                                        <Input
                                            value={customName}
                                            onChange={(e) => setCustomName(e.target.value)}
                                            placeholder="e.g. Shiv Gauri Agencies"
                                            className="mt-1"
                                            data-testid="vendor-rmm-dn-custom-input"
                                        />
                                    )}
                                </div>
                            )}

                            <p className="text-xs text-muted-foreground">
                                All bills &amp; alerts on <b>{currentVendor?.vendor}</b> will move to
                                the chosen vendor, and price alerts will be recomputed.
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        data-testid="vendor-rmm-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        data-testid="vendor-rmm-submit"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={14} className="mr-1 animate-spin" />
                                Saving…
                            </>
                        ) : mode === 'rename' ? (
                            'Rename vendor'
                        ) : (
                            'Merge vendor'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
